import React, {
  useEffect, useState, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import {
  closestCenter,
  DragOverlay,
  DndContext,
  defaultDropAnimation,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

import {
  deleteRoomValidationResult,
} from '@redux/modules/formGeneration';

import { setRepeatableSectionCount } from '@redux/modules/listing';

import Grid from '@components/form/formComponents/imageCropper/imageDropZone/components/Grid';
import RoomCard from '@components/form/formComponents/repeatableRoom/RoomCard';

const defaultDropAnimationConfig = {
  ...defaultDropAnimation,
  dragSourceOpacity: 0.5,
};

export function RoomsGridList({
  activationConstraint,
  animateLayoutChanges,
  adjustScale = false,
  collisionDetection = closestCenter,
  dropAnimation = defaultDropAnimationConfig,
  formik,
  getItemStyles = () => ({}),
  handle = false,
  isDisabled = () => false,
  measuring,
  modifiers,
  renderItem,
  strategy = rectSortingStrategy,
  useDragOverlay = true,
  wrapperStyle = () => ({}),
}) {
  const roomValues = formik?.values?.Rooms;
  const [items, setItems] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint,
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const dispatch = useDispatch();
  const entryItems = useMemo(() => {
    const arrayOfItems = items ? Object.entries(items) : [];
    return arrayOfItems;
  }, [items]);

  const getIndex = id => Object.keys(items).findIndex(item => item === id);
  const activeIndex = activeId ? getIndex(activeId) : -1;

  useEffect(() => {
    if (roomValues) {
      setItems(roomValues);
    }
  }, [roomValues]);

  const onRemoveRoom = id => {
    // remove room from formik values
    const { [id]: removedValue, ...otherValues } = items;
    // otherValues is a dictionary.
    // keys are just uid prop

    // eslint-disable-next-line no-param-reassign, no-return-assign
    const sortedOthers = Object.values(otherValues).filter(ov => !ov.IsDeleted).sort((ov1, ov2) => ov1.SortOrder - ov2.SortOrder).map((sOv, index) => sOv = { ...sOv, SortOrder: index + 1 });
    const otherDict = {};
    const newValues = formik.values;
    sortedOthers.forEach(sO => {
      otherDict[sO.uid] = sO;
      newValues.Rooms[sO.uid].SortOrder = sO.SortOrder;
    });
    setItems({ ...otherDict });
    newValues.Rooms[removedValue.uid].IsDeleted = true;
    formik.setValues(newValues);
    // remove room from formik touched, if it exists
    if (formik?.touched?.Rooms?.[id]) {
      const { [id]: removedTouched, ...otherTouched } = formik.touched.Rooms;
      formik.setTouched({
        ...formik.touched,
        Rooms: { ...otherTouched },
      });
    }
    // remove room from redux validation
    dispatch(setRepeatableSectionCount(newValues));
    dispatch(deleteRoomValidationResult(id));
  };

  const spliceDict = (dict, startIndex, endIndex) => {
    let newDict = [];
    const newArr = {};
    Object.entries(dict).map(([key]) => newDict.push({ [key]: dict[key] }));
    newDict = arrayMove(newDict, startIndex, endIndex);
    newDict.map((entry, index) => {
      Object.entries(entry).map(([key, value]) => {
        newArr[key] = {
          ...value,
          SortOrder: index + 1,
        };
        return undefined;
      });
      return undefined;
    });
    return newArr;
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={({ active }) => {
        if (!active) {
          return;
        }
        setActiveId(active.id);
      }}
      onDragEnd={({ over }) => {
        setActiveId(null);

        if (over) {
          const overIndex = getIndex(over.id);
          if (activeIndex !== overIndex) {
            const newRooms = spliceDict(items, activeIndex, overIndex);
            formik.setValues({
              ...formik.values,
              Rooms: { ...newRooms },
            });
          }
        }
      }}
      onDragCancel={() => setActiveId(null)}
      measuring={measuring}
      modifiers={modifiers}
    >
      <SortableContext items={entryItems} strategy={strategy}>
        <Grid columns={2} className="grid grid-2-col">
          {items && entryItems.map(([k], index) => (items[k].IsDeleted
            ? null
            : (
              <SortableRoomCard
                key={k}
                id={k}
                handle={handle}
                index={index}
                formik={formik}
                style={getItemStyles}
                wrapperStyle={wrapperStyle}
                disabled={isDisabled(k)}
                renderItem={renderItem}
                onRemoveRoom={onRemoveRoom}
                animateLayoutChanges={animateLayoutChanges}
                useDragOverlay={useDragOverlay}
              />
            )
          ))}
        </Grid>
      </SortableContext>
      {useDragOverlay
        && createPortal(
          <DragOverlay
            adjustScale={adjustScale}
            dropAnimation={dropAnimation}
            handle
          >
            {activeId && (
              <RoomCard
                id={activeId}
                handle={handle}
                renderItem={renderItem}
                formik={formik}
                wrapperStyle={wrapperStyle({
                  index: activeIndex,
                  isDragging: true,
                  id: items[activeIndex],
                })}
                style={getItemStyles({
                  id: items[activeIndex],
                  index: activeIndex,
                  isSorting: activeId !== null,
                  isDragging: true,
                  overIndex: -1,
                  isDragOverlay: true,
                })}
                dragOverlay
              />
            )}
          </DragOverlay>,
          document.body,
        )}
    </DndContext>
  );
}

export function SortableRoomCard({
  animateLayoutChanges,
  formik,
  disabled,
  id,
  index,
  handle,
  onRemoveRoom,
  style,
  renderItem,
  useDragOverlay,
  wrapperStyle,
}) {
  const {
    attributes,
    isDragging,
    isSorting,
    listeners,
    overIndex,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    animateLayoutChanges,
    id,
    disabled,
  });
  return (
    <RoomCard
      ref={setNodeRef}
      id={id}
      value={id}
      disabled={disabled}
      dragging={isDragging}
      sorting={isSorting}
      handle={handle}
      renderItem={renderItem}
      index={index}
      formik={formik}
      style={style({
        index,
        id,
        isDragging,
        isSorting,
        overIndex,
      })}
      onRemoveRoom={onRemoveRoom}
      transform={transform}
      transition={!useDragOverlay && isDragging ? 'none' : transition}
      wrapperStyle={wrapperStyle({ index, isDragging, id })}
      data-index={index}
      data-id={id}
      dragOverlay={!useDragOverlay && isDragging}
      listeners={listeners}
      {...attributes}
    />
  );
}

RoomsGridList.propTypes = {
  activationConstraint: PropTypes.number,
  animateLayoutChanges: PropTypes.number,
  adjustScale: PropTypes.bool,
  collisionDetection: PropTypes.string,
  dropAnimation: PropTypes.number,
  formik: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.bool, PropTypes.shape(), PropTypes.number, PropTypes.func])),
  getItemStyles: PropTypes.func,
  handle: PropTypes.bool,
  isDisabled: PropTypes.number,
  measuring: PropTypes.number,
  modifiers: PropTypes.number,
  renderItem: PropTypes.arrayOf(PropTypes.string, PropTypes.shape()),
  strategy: PropTypes.arrayOf(PropTypes.string, PropTypes.shape()),
  useDragOverlay: PropTypes.bool,
  wrapperStyle: PropTypes.func,
};

SortableRoomCard.propTypes = {
  animateLayoutChanges: PropTypes.number,
  disabled: PropTypes.bool,
  formik: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.bool, PropTypes.shape(), PropTypes.number, PropTypes.func])),
  handle: PropTypes.bool,
  isSelected: PropTypes.bool,
  id: PropTypes.string,
  index: PropTypes.number,
  renderItem: PropTypes.arrayOf(PropTypes.string, PropTypes.shape()),
  onRemoveRoom: PropTypes.func,
  style: PropTypes.func,
  useDragOverlay: PropTypes.bool,
  wrapperStyle: PropTypes.func,
};
